import { Inject, Injectable } from '@angular/core';
import { APP_ENVIRONMENT } from '@shared/providers/environment.provider';
import { MapDirectionsService, MapGeocoder } from '@angular/google-maps';
import { from, map, Observable, shareReplay, switchMap } from 'rxjs';
import { roundDecimal } from '@shared/utils/math';
import { getLetterByIndex } from '@shared/utils/common';
import { buildRouteRequest } from '@shared/utils/google';
import { Environment } from '../../types/environment';
import { PointWithLabel } from '../../types/location';

export type RoutePartDetails = { route: string; length: string };
export type Geo = google.maps.GeocoderResult;
@Injectable()
export class GoogleMapService {
  private readonly pinElementCtor$ = from(this.loadPinElement()).pipe(
    shareReplay(1),
  );

  constructor(
    @Inject(APP_ENVIRONMENT) private readonly env: Environment,
    private readonly geocoder: MapGeocoder,
    private readonly mapDirectionsService: MapDirectionsService,
  ) {}

  getInitialMapOptions(): google.maps.MapOptions {
    return {
      zoom: 4,
      center: { lat: 53.893009, lng: 27.567444 },
      fullscreenControl: false,
      keyboardShortcuts: false,
      mapTypeControl: false,
    };
  }

  async loadAddress(lat: number, lng: number) {
    const geocoder = new google.maps.Geocoder();
    const address = await new Promise<Nullable<string>>(resolve => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          resolve(results[0].formatted_address);
        } else {
          resolve(null);
        }
      });
    });
    return address;
  }

  loadPinElement() {
    const lib = google.maps.importLibrary(
      'marker',
    ) as Promise<google.maps.MarkerLibrary>;
    return lib.then(({ PinElement }) => PinElement);
  }

  withPinElementConstructor<T>(
    source: Observable<T>,
  ): Observable<[T, typeof google.maps.marker.PinElement]> {
    return source.pipe(
      switchMap(data =>
        this.pinElementCtor$.pipe(
          map(
            pinElement =>
              [data, pinElement] as [T, typeof google.maps.marker.PinElement],
          ),
        ),
      ),
    );
  }

  get mapId() {
    return this.env.googleMapId;
  }

  geocodeByAddress(address: string) {
    return this.geocoder.geocode({
      address,
    });
  }

  getRoutePartsLength(
    routes: google.maps.DirectionsRoute[],
    validPoints: PointWithLabel[],
  ) {
    const legs: google.maps.DirectionsLeg[] = [];

    routes.forEach(route => {
      route.legs.forEach(leg => {
        legs.push(leg);
      });
    });

    return legs.map((leg, index) => {
      const startLabel = validPoints[index]?.label ?? getLetterByIndex(index);
      const endLabel =
        validPoints[index + 1]?.label ?? getLetterByIndex(index + 1);

      return {
        route: `${startLabel}-${endLabel}`,
        length: leg.distance?.text ?? '',
      };
    });
  }

  getSecondaryPinElementOptions(
    glyph = '',
  ): google.maps.marker.PinElementOptions {
    return {
      glyph,
      background: 'rgba(25,189,211,0.5)',
      borderColor: 'rgba(25,84,211,0.82)',
      glyphColor: 'rgba(255,255,255,1)',
      scale: 0.9,
    };
  }

  getDirectionsResult(routes: number[][]) {
    return this.mapDirectionsService
      .route(buildRouteRequest(routes))
      .pipe(map(response => response.result));
  }

  getFullRouteLength(routes: google.maps.DirectionsRoute[]) {
    const meters =
      routes.reduce((prevRouteLength, currentRoute) => {
        const currentRouteLength = currentRoute.legs.reduce(
          (prevLegsLength, currentLeg) => {
            return prevLegsLength + (currentLeg.distance?.value ?? 0);
          },
          0,
        );
        return prevRouteLength + currentRouteLength;
      }, 0) ?? 0;
    return roundDecimal(meters / 1000);
  }
}
